import type {
  PackEntity,
  PackItemEntity,
  PackProductSnapshot,
  PackVariantSnapshot,
} from '@/domain/entities/pack.entity';

export interface PackSlot {
  slotKey: string;
  packItemId: string;
  productId: string;
  product: PackProductSnapshot;
  defaultVariantId?: string;
}

function expandPackItemSlots(item: PackItemEntity): PackSlot[] {
  const product = item.product;
  if (!product) return [];

  const count = Math.max(1, item.quantity);
  return Array.from({ length: count }, (_, index) => ({
    slotKey: count > 1 ? `${item.id}#${index}` : item.id,
    packItemId: item.id,
    productId: item.product_id,
    product,
    defaultVariantId: item.variant_id,
  }));
}

/** Build selector slots for a fixed pack (one row per pack item × quantity). */
export function buildFixedPackSlots(pack: PackEntity): PackSlot[] {
  return pack.items.flatMap(expandPackItemSlots);
}

/** Build selector slots for selected configurable pack items only. */
export function buildConfigurablePackSlots(
  pack: PackEntity,
  selectedPackItemIds: string[],
): PackSlot[] {
  const selected = new Set(selectedPackItemIds);
  return pack.items.filter((item) => selected.has(item.id)).flatMap(expandPackItemSlots);
}

export function pickDefaultVariantId(
  slot: PackSlot,
  variants: PackVariantSnapshot[],
): string | undefined {
  if (slot.defaultVariantId && variants.some((v) => v.id === slot.defaultVariantId && v.enabled)) {
    return slot.defaultVariantId;
  }
  return variants.find((v) => v.enabled)?.id;
}
